import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ProjectElement, ProjectElementSelectOption } from '../project-element.interface';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { ConstantsService } from '../../../utilities/constants/constants.service';
import { ProjectModule } from '../../module/project-module.interface';
import { ModuleService } from '../../module/module.service';
import { ProjectSubmodule } from '../../submodule/project-submodule.interface';
import { SubmoduleService } from '../../submodule/submodule.service';
import {
  ConstantsProjectLevel,
  ConstantsKnowledgeLevel,
  ConstantsUnitType,
  ConstantsElementType,
  ConstantsRequirementStrictness,
  ConstantsRequirementType
} from '../../../utilities/constants/constants.interface';
import { distinctUntilChanged, filter, takeUntil, skip } from 'rxjs/operators';
import { orderBy, isEqual } from 'lodash-es';
import { ProjectService } from '../../project/project.service';
import { BuildingService } from '../../building/building.service';
import { AreaService } from '../../area/area.service';
import { BasicAreaService } from '../../basic-area/basic-area.service';
import { ModalService } from '../../../services/modal/modal.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ElementService } from '../element.service';
import { RouterService } from '../../../utilities/router/router.service';
import { ElementSidebarService } from '../element-sidebar/element-sidebar.service';
import { combineLatest, Subject } from 'rxjs';
import { ElementFormService } from './element-form.service';
import { AttachmentService } from '../../../services/attachment/attachment.service';
import { Editor } from 'tinymce';
import { TinymceService } from '../../../services/tinymce/tinymce.service';
import { TemplateFormControls } from '../element-sidebar/element.sidebar.interface';
import { AuthService } from '../../../auth/auth.service';


@Component({
  selector: 'sg2-element-form',
  templateUrl: './element-form.component.html',
  styleUrls: ['./element-form.component.less']
})
export class ElementFormComponent implements OnDestroy, OnInit {

  /** Display. */
  @Input() display: 'page' | 'column';

  /** Current active element. */
  @Input() element: ProjectElement;

  /** Optional element form controls. */
  @Input() elementFormControls: TemplateFormControls<ProjectElement>;

  /** Current mode. */
  @Input() mode: 'create' | 'read' | 'update' = 'update';

  /** The delay to check if the form is dirty. */
  private readonly FORM_POPULATE_DELAY = 500;

  /** Unsubscribe hook. */
  private destroy$ = new Subject();

  /** Active editor instane. */
  private editor: Editor;

  /** Form. */
  public form: FormGroup;

  /** Layout control. */
  public half: number;
  public third: number;
  public direction: 'column' | 'row';

  /** Constants. */
  public buildings: any[];
  public constructions: any[];
  public inputTypes: ConstantsElementType[];
  public knowledgeLevels: ConstantsKnowledgeLevel[];
  public modules: ProjectModule[];
  public options: ProjectElementSelectOption[];
  public projectLevels: ConstantsProjectLevel[];
  public requirementStrictnesses: ConstantsRequirementStrictness[];
  public requirementTypes: ConstantsRequirementType[];
  public submodules: ProjectSubmodule[];
  public units: ConstantsUnitType[];

  /** TinyMCE config. */
  public tinyConfig: any;

  constructor(
    private areaService: AreaService,
    private attachmentService: AttachmentService,
    private authService: AuthService,
    private basicAreaService: BasicAreaService,
    private buildingService: BuildingService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    private elementFormService: ElementFormService,
    private elementSidebarService: ElementSidebarService,
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private moduleService: ModuleService,
    private projectService: ProjectService,
    private routerService: RouterService,
    private submoduleService: SubmoduleService,
    public tinymceService: TinymceService,
    private translateService: TranslateService
  ) {
    this._initTinyMCE();
    this._onElementChange();
  }

  ngOnInit() {
    // Populate dropdown lists.
    this.inputTypes = this.constantsService.getElementTypes('displayName');
    this.knowledgeLevels = this.constantsService.getKnowledgeLevels('displayName');
    this.modules = this.moduleService.get();
    this.options = (this.element.selectOptions || []).map(o => ({ ...o, id: +o.id }) );
    this.projectLevels = this.constantsService.getProjectLevels();
    this.requirementStrictnesses = this.constantsService.getRequirementStrictness('displayName');
    this.requirementTypes = this.constantsService.getRequirementTypes('displayName');
    this.units = this.constantsService.getUnitTypes();

    // Setup.
    this.form = this._getForm();
    this.elementFormService.setIsDirty(false);

    // Subscribe to form fields.
    this._onProjectLevelChange();
    this._onProjectLevelOrRequirementTypeChange();
    this._onKnowledgeLevelChange();
    this._onModuleIdChange();
    this._onInputTypeChange();
    this._onHasInputInstructionChange();
    this._onFormChange();
    this._onFormControlAction();

    // Set basic values.
    // Triggers form supscriptions, which will populate the rest of the form.
    this.form.get('name').setValue(this.element.name || this.translateService.get('newSpecification'));
    this.form.get('projectLevel').setValue(this.element.projectLevel || 1);
    this.form.get('moduleId').setValue(this.element.moduleId || this.moduleService.get()[0]._id);
    this.form.get('hasInputInstruction').setValue(this.element.hasInputInstruction || false);

    // Set mode.
    this._setMode();
    this._setViewsize();
  }

  ngOnDestroy() {
    this.elementFormService.setIsDirty(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize form.
   *
   * @returns Initialized form group.
   */
  private _getForm(): FormGroup {
    const template = {
      name: this.element.name,
      projectLevel: '',
      constructionId: '',
      knowledgeLevel: '',
      moduleId: '',
      submoduleId: '',
      inputType: '',
      hasInputInstruction: false,
      inputInstruction: '',
      requirementType: '',
      requirementStrictness: '',
      value: '',
      unit: ''
    };

    // Enhance template with form controls (when given).
    for (const [property, controls] of Object.entries(this.elementFormControls || {})) {
      template[property] = controls;
    }

    return this.formBuilder.group(template);
  }

  /**
   * Add a new option to the Selection list.
   *
   * @todo Prevent submission of a value that already exists.
   */
  public addOption(): void {
    this.modalService.prompt(this.translateService.get('addoption')).afterClosed().subscribe(value => {
      if (value) {
        const option = {
          active: true,
          id: this.options.length + 1,
          name: value
        };
        this.options.push(option);
        this.element.selectOptions.push(option);
      }
      this.form.get('value').setValue(value || this.element.value || this.options[0].name);
    });
  }

  /**
   * Delete option from list.
   *
   * @param value The selected value.
   * @param $event The event fired from the click.
   */
  public deleteOption(value: ProjectElementSelectOption['name'], $event: Event): void {
    $event.stopPropagation();
    if (value !== this.form.get('value').value) {
      this.options = this.options.filter(o => o.name !== value);
    }
  }

  /**
   * Get form value.
   *
   * Patches a bug in the tinymce-angular lib where the formControl is not updated after
   * the upload of a dropped image is completed. When the input type is set to 'paragraph'
   * (Long String), fetch the value directly from the TinyMCE editor, to make sure it's
   * the latest version.
   */
  private _getFormValue(): Partial<ProjectElement> {
    return {
      ...this.form.value,
      value: this.form.get('inputType').value === 'Long String' && this.editor
        ? this.tinymceService.sanitizeOutput(this.editor.getContent())
        : this.form.get('value').value
    };
  }

  /**
   * Initialize TinyMCE config.
   *
   * Since the TinyMCE is based on ReactiveForm instead of NgModel, use the `init_instance_callback` to
   * check whether the editor is dirty, so the form control buttons can be activated
   *
   * @note `extended_valid_elements` is there to prevent replacing <b> by <strong>,
   *  and <i> by <em> on editing, since it would wrongly trigger isChanged/DiffChecker.
   * @note `formats` is there for consistency with template values.
   */
  private _initTinyMCE(): void {
    this.tinyConfig = {
      branding: false,
      formats: {
        bold: { inline: 'b' },
        italic: { inline: 'i' }
      },
      height: 250,
      images_reuse_filename: true,
      images_upload_handler: (blobInfo, success, failure) => {
        this.attachmentService.uploadImageElement(blobInfo.filename(), blobInfo.base64()).subscribe(resolve => {
          success(`/api/project/v2/${ this.projectService.getId() }/element/image/${ resolve.location }`);
        });
      },
      init_instance_callback: (editor: Editor) => {
        this.editor = editor;
        editor.on('Dirty', (e) => {
          this.elementFormService.setIsDirty(true);
        });
      },
      menubar: false,
      nonbreaking_force_tab: true,
      paste_data_images: true,
      plugins: 'paste table lists textcolor nonbreaking code',
      toolbar: 'fontselect | fontsizeselect | bold italic underline strikethrough '
      + '| forecolor backcolor | bullist numlist | alignleft aligncenter alignright alignjustify | table'
      + (this.authService.isAdmin() ? ' | code' : ''),
      ui_container: '#editor-container',
      extended_valid_elements: 'b,strong,em,i'
    };
  }

  /**
   * Keep track of external changes to the element.
   *
   * @note Has some protection to prevent an infinite loop in an edge case where this component
   * is visible *twice*, such as opening the element sidebar on the element page. Otherwise
   * the two forms will update each other until the memory overflows.
   */
  private _onElementChange(): void {
    this.elementService.element$.pipe(
      skip(1),
      takeUntil(this.destroy$),
      filter(e => !this.elementFormService.isDirty()),
    ).subscribe(() => {
      const updatedElement = this.elementService.getElement(this.element._id, { identifier: this.element.identifier });
      this.element = updatedElement;
    });
  }

  /**
   * Check for form changes.
   *
   * When the form notices a change it sets the form to dirty in the
   * form control service, which actives the *save* and *cancel* buttons.
   *
   * @note There's a built in delay, to prevent the form getting dirty because
   *  of its dynamic population.
   */
  private _onFormChange(): void {
    setTimeout(() => {
      this.form.valueChanges.pipe(
        distinctUntilChanged(isEqual)
      ).subscribe(changes => {
        this.elementFormService.setIsDirty(true);
      });
    }, this.FORM_POPULATE_DELAY);
  }

  /**
   * On new form action.
   *
   * - `cancel`: Just close the sidebar (auto reverts elements).
   * - `submit`: Patch element, and hide controls until next change.
   */
  private _onFormControlAction(): void {
    this.elementFormService.action$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(action => {
      // On cancel
      if (action === 'cancel') {
        delete this.element;
        this.elementSidebarService.dismiss();
      }
      // On submit
      if (action === 'submit') {
        // On create
        if (this.mode === 'create') {
          this.elementService.add({
            ...this.element,    // Template with default values
            ...this._getFormValue()  // Current form values
          }).pipe(
            takeUntil(this.destroy$)
          ).subscribe(result => {
            this.elementSidebarService.dismiss();
          });
        }
        // On patch
        if (this.mode === 'update') {
          this.elementService.patch(this.element, {
            selectOptions: this.element.selectOptions,
            ...this._getFormValue()
          }).pipe(
            takeUntil(this.destroy$)
          ).subscribe(result => {
            this.elementSidebarService.dismiss();
          });
        }
      }
    });
  }

  /**
   * Track has input instruction form field.
   */
  private _onHasInputInstructionChange(): void {
    this.form.get('hasInputInstruction').valueChanges.pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(hasInputInstruction => {
      this.form.get('inputInstruction').setValue(this.element.inputInstruction || false);
    });
  }

  /**
   * Track input type form field.
   */
  private _onInputTypeChange(): void {
    this.form.get('inputType').valueChanges.pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(inputType => {
      this._setUnit(inputType);
      this._setValue(inputType);
    });
  }

  /**
   * Track knowledge level form field.
   */
  private _onKnowledgeLevelChange(): void {
    this.form.get('knowledgeLevel').valueChanges.pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(knowledgeLevel => {
      this._setStrictness(knowledgeLevel);
    });
  }

  /**
   * Track module form field.
   */
  private _onModuleIdChange(): void {
    this.form.get('moduleId').valueChanges.pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(moduleId => {
      this._setSubmodules(moduleId);
    });
  }

  /**
   * Track project level form field.
   *
   * @todo Sort areas, make clear in which cluster they are.
   */
  private _onProjectLevelChange(): void {
    this.form.get('projectLevel').valueChanges.pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(projectLevel => {
      this._setConstructions(projectLevel);
      this._setKnowledgeLevel(projectLevel);
    });
  }

  /**
   * On project level OR requirement type change.
   */
  private _onProjectLevelOrRequirementTypeChange(): void {
    combineLatest([
      this.form.get('projectLevel').valueChanges,
      this.form.get('requirementType').valueChanges
    ]).pipe(
      distinctUntilChanged(isEqual)
    ).subscribe(values => {
      const [projectLevel, requirementType] = values;
      this._setInputType(projectLevel, requirementType);
    });
  }

  /**
   * Set constructions.
   *
   * @todo Set value based on current element value.
   *
   * @param [projectLevel=1] The project level to set the list of constructions for.
   */
  private _setConstructions(projectLevel: ConstantsProjectLevel['_id'] = 1) {
    let constructions = [];
    switch (projectLevel) {
      case 1: // Project
        constructions = [this.projectService.get()];
        break;
      case 2: // Building
        constructions = this.buildingService.get();
        break;
      case 4: // Basic area
        constructions = this.basicAreaService.get();
        break;
      case 5: // Area
        constructions = this.areaService.get();
        break;
    }
    this.constructions = orderBy(constructions, 'name');
    const current = this.constructions.findIndex(c => c._id === this.element.constructionId);
    this.form.get('constructionId').setValue(this.constructions[current > -1 ? current : 0]._id);
  }

  /**
   * Set Input type.
   *
   * @param projectLevel The project level to determine the input type.
   * @param requirementType The requirement type to determine the input type.
   */
  private _setInputType(projectLevel: ConstantsProjectLevel['_id'] = 1, requirementType: ConstantsRequirementType['_id'] = 1): void {
    this.inputTypes = this.constantsService.getElementTypes('displayName');
    switch (true) {
      // Project or Building
      case (projectLevel === 1 || projectLevel === 2):
        this.inputTypes = this.inputTypes.filter(k => [2].indexOf(k._id) > -1);
        break;
      // Basic area and Technical requirement
      case (projectLevel === 4 && requirementType === 2):
        this.inputTypes = this.inputTypes.filter(k => [1, 2, 3, 4].indexOf(k._id) > -1);
        break;
      // Area and Technical requirement
      case (projectLevel === 5 && requirementType === 2):
        this.inputTypes = this.inputTypes.filter(k => [1, 2, 3, 4].indexOf(k._id) > -1);
        break;
      // Basic area and not Technical requirement
      case (projectLevel === 4 && requirementType !== 2):
        this.inputTypes = this.inputTypes.filter(k => [2].indexOf(k._id) > -1);
        break;
      // Area and not Technical requirement
      case (projectLevel === 5 && requirementType !== 2):
        this.inputTypes = this.inputTypes.filter(k => [2].indexOf(k._id) > -1);
        break;
    }
    const current = this.inputTypes.findIndex(i => i.value === this.element.inputType);
    this.form.get('inputType').setValue(this.inputTypes[current > -1 ? current : 0].value);
  }

  /**
   * Set knowledge levels.
   *
   * @param projectLevel The project level to generate the list of knowledge levels for.
   */
  private _setKnowledgeLevel(projectLevel: ConstantsProjectLevel['_id']): void {
    this.knowledgeLevels = this.constantsService.getKnowledgeLevels('displayName');
    switch (projectLevel) {
      case 1: // Project
      this.knowledgeLevels = this.knowledgeLevels.filter(k => [1, 2].indexOf(k._id) > -1);
      break;
      case 2: // Building
      this.knowledgeLevels = this.knowledgeLevels.filter(k => [2, 3, 4, 5].indexOf(k._id) > -1);
      break;
      case 4: // Basic area
      this.knowledgeLevels = this.knowledgeLevels.filter(k => [3].indexOf(k._id) > -1);
      break;
      case 5: // Area
      this.knowledgeLevels = this.knowledgeLevels.filter(k => [3].indexOf(k._id) > -1);
      break;
    }
    const current = this.knowledgeLevels.findIndex(k => k._id === this.element.knowledgeLevel);
    this.form.get('knowledgeLevel').setValue(this.knowledgeLevels[current > -1 ? current : 0]._id);
  }

  /**
   * Set mode.
   *
   * - `read`: Disable all fields.
   * - `update`: Disable fields that may only be set during creating.
   *
   * @note Add a 'create' mode.
   */
  private _setMode(): void {
    if (this.mode === 'create') {
      this.elementFormService.setIsDirty(true);
    }
    if (this.mode === 'read') {
      Object.entries(this.form.value).forEach((k, v) => {
        this.form.get(k[0]).disable();
      });
    }
    if (this.mode === 'update') {
      Object.entries(this.form.value).forEach((k, v) => {
        if (['projectLevel', 'constructionId', 'moduleId', 'submoduleId', 'inputInstruction'].indexOf(k[0]) > -1) {
          this.form.get(k[0]).disable();
        }
      });
    }
  }

  /**
   * Set strictness.
   *
   * @param knowledgeLevel Enable/disable strictness form field based on knowledge level.
   */
  private _setStrictness(knowledgeLevel: ConstantsKnowledgeLevel['_id']): void {
    // Toggle strictness form fields.
    if (knowledgeLevel === 3) {
      this.form.get('requirementType').enable({ emitEvent: false });
      this.form.get('requirementStrictness').enable({ emitEvent: false });
    } else {
      this.form.get('requirementType').disable({ emitEvent: false });
      this.form.get('requirementStrictness').disable({ emitEvent: false });
    }
    // Set strictess type.
    const currentType = this.requirementTypes.findIndex(k => k._id === this.element.requirementType);
    this.form.get('requirementType').setValue(this.requirementTypes[currentType > -1 ? currentType : 0]._id);
    // Set strictness requirement.
    const currentStrictness = this.requirementStrictnesses.findIndex(k => k._id === this.element.requirementStrictness);
    this.form.get('requirementStrictness').setValue(this.requirementStrictnesses[currentStrictness > -1 ? currentStrictness : 0]._id);
  }

  /**
   * Set submodules.
   *
   * By default, set the current submodule of the element. If the current submodule is not
   * in the selected module, then select the first submodule in the list.
   *
   * Upon switching module, perform a check if the selected module is the element's current
   * module, if so, select the current submodule.
   *
   * @param moduleId Parent module id.
   */
  private _setSubmodules(moduleId: ProjectModule['_id']): void {
    this.submodules = this.moduleService.getModule(moduleId).submodules;
    const currentSubmodule = this.element.moduleId === moduleId
      ? this.submodules.findIndex(s => s._id === this.element.submoduleId)
      : 0;
    this.form.get('submoduleId').setValue(this.submodules[currentSubmodule]._id);
  }

  /**
   * Set value unit.
   *
   * By default, set the element's current unit. If there is none (because input type is
   * a paragraph or a list), then select the first from the list.
   *
   * @param inputType The chosen input type.
   */
  private _setUnit(inputType: ProjectElement['inputType']): void {
    this.form.get('unit').setValue(this.element.unit && ['Number', 'String'].indexOf(inputType) > -1
      ? this.units.find(u => u.value === this.element.unit).value
      : this.units[0].value
    );
  }

  /**
   * Set value.
   *
   * @param inputType The chosen input type.
   */
  private _setValue(inputType: ProjectElement['inputType']): void {
    this.form.get('value').setValue(inputType === this.element.inputType
      ? this.element.value
      : null
    );
  }

  /**
   * Set column size.
   */
  private _setViewsize() {
    this.half = this.display === 'page' ? 50 : 100;
    this.third = this.display === 'page' ? 33 : 100;
    this.direction = this.display === 'page' ? 'row' : 'column';
  }
}
